import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="turbo-frame-refresh"
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 3000 }, // Default 3 seconds
    duration: { type: Number, default: 60000 }, // Default 60 seconds
    settledSelector: { type: String, default: "[data-provisioning-settled='true']" }
  }

  connect() {
    console.log("Turbo Frame Refresh controller connected")
    console.log(`Polling every ${this.intervalValue}ms for ${this.durationValue}ms`)

    this.startTime = Date.now()

    // MS50 #2987: opt-in content-stop. When a (re)loaded frame reports a terminal
    // state via [data-provisioning-settled="true"], stop polling immediately
    // instead of waiting out the duration window. Frames that never render the
    // marker (e.g. actions, recent-activity) are unaffected and keep the timer
    // behavior. Re-check on every turbo:frame-load so a settled reload stops us.
    this.boundCheckSettled = this.checkSettled.bind(this)
    this.element.addEventListener("turbo:frame-load", this.boundCheckSettled)

    // A frame whose server-rendered content is already settled must not poll.
    if (this.isSettled()) {
      console.log("Provisioning already settled - not starting turbo-frame polling")
      return
    }

    this.startPolling()
  }

  disconnect() {
    this.stopPolling()
    if (this.boundCheckSettled) {
      this.element.removeEventListener("turbo:frame-load", this.boundCheckSettled)
    }
  }

  // True when the frame content advertises a terminal provisioning state.
  // Absence of the marker (or data-provisioning-settled="false") returns false,
  // so this is strictly opt-in — non-provisioning frames never match.
  isSettled() {
    return !!this.element.querySelector(this.settledSelectorValue)
  }

  // turbo:frame-load handler: stop polling once the (re)loaded content is settled.
  checkSettled() {
    if (this.isSettled()) {
      console.log("Provisioning settled - stopping turbo-frame polling")
      this.stopPolling()
    }
  }

  startPolling() {
    this.pollTimer = setInterval(() => {
      // Issue #473: Skip polling when tab is hidden to reduce connection pool exhaustion
      // Page Visibility API prevents unnecessary server requests when user isn't viewing the tab
      if (document.hidden) {
        console.log("Skipping turbo-frame poll - tab is hidden")
        return
      }

      // MS50 #2987: stop as soon as the content reports a terminal (settled) state.
      if (this.isSettled()) {
        console.log("Provisioning settled - stopping turbo-frame polling")
        this.stopPolling()
        return
      }

      const elapsed = Date.now() - this.startTime

      // Stop polling after duration expires
      if (elapsed > this.durationValue) {
        console.log(`Polling duration expired after ${elapsed}ms`)
        this.stopPolling()
        return
      }

      // Find the turbo-frame element
      const frame = this.element.querySelector('turbo-frame')
      if (frame && frame.src) {
        console.log(`Refreshing turbo-frame: ${frame.id}`)

        // Remove the 'complete' attribute to trigger a reload
        // Turbo adds this attribute after loading; removing it forces a reload
        frame.removeAttribute('complete')

        // Trigger reload by calling frame.reload() if available
        if (typeof frame.reload === 'function') {
          frame.reload()
        } else {
          // Fallback: force reload by temporarily removing/re-adding src
          const src = frame.src
          frame.removeAttribute('src')
          setTimeout(() => {
            frame.setAttribute('src', src)
          }, 10)
        }
      }
    }, this.intervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      console.log("Stopping turbo-frame polling")
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }
}
